/**
 * Extracts the tenant identifier from the current URL.
 * Expected formats: 
 * - tenant.smartinterview.com
 * - tenant.localhost
 * 
 * Returns the tenant string, or null if no tenant is found.
 */
export const getTenantFromUrl = () => {
  if (typeof window === 'undefined') return null;
  
  const hostname = window.location.hostname;
  let tenant = null;
  
  const parts = hostname.split('.');
  
  // If there are at least 3 parts (e.g. tenant.domain.com)
  if (parts.length >= 3) {
    tenant = parts[0];
  }
  
  // For local testing like tenant.localhost
  else if (parts.length === 2 && parts[1] === 'localhost') {
    tenant = parts[0];
  }

  // Fallback: Check localStorage if no subdomain in URL (useful for local development on localhost:3000)
  if (!tenant) {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const user = JSON.parse(rawUser);
        if (user && user.subdomain) {
          tenant = user.subdomain;
        }
      }
    } catch (e) {
      console.error('Error parsing user from localStorage for tenant fallback', e);
    }
  }

  return tenant;
};

/**
 * Redirects the user to their organization-specific subdomain if they are not already on it.
 * Supports localhost and production domains (virtualselect.in).
 * 
 * Returns true if redirecting, false otherwise.
 */
export const handleTenantRedirect = (user) => {
  if (!user || !user.subdomain) return false;

  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  const currentSubdomain = hostname.split('.')[0];

  // Determine if we are already on the correct subdomain
  const isAlreadyOnSubdomain = currentSubdomain === user.subdomain || 
                               hostname === `${user.subdomain}.localhost` ||
                               hostname === `${user.subdomain}.virtualselect.in`;

  if (isAlreadyOnSubdomain) {
    return false;
  }

  let targetHost = null;

  if (hostname.endsWith('localhost')) {
    targetHost = `${user.subdomain}.localhost`;
  } else if (hostname.endsWith('virtualselect.in')) {
    targetHost = `${user.subdomain}.virtualselect.in`;
  }

  if (targetHost) {
    const role = user.user_type || user.role;
    let targetPath = '/interview';
    if (role === 'CANDIDATE') {
      targetPath = '/candidate-dashboard';
    } else if (role === 'BU_HEAD') {
      targetPath = '/bu-dashboard';
    } else if (role === 'MANAGER') {
      targetPath = '/manager-dashboard';
    } else if (role === 'INTERVIEWER') {
      targetPath = '/interviewer-dashboard';
    } else if (role === 'HR') {
      targetPath = '/hr-dashboard';
    }

    const encodedUser = window.btoa(unescape(encodeURIComponent(JSON.stringify(user))));
    const targetUrl = `${protocol}//${targetHost}${port}${targetPath}?auth_data=${encodeURIComponent(encodedUser)}`;
    window.location.href = targetUrl;
    return true;
  }

  return false;
};
